import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export type InventoryStockStatus =
  | "in_stock"
  | "low_stock"
  | "out_of_stock"
  | "disabled";

export type InventoryMovementType =
  | "stock_created"
  | "stock_adjusted"
  | "stock_reserved"
  | "reservation_released"
  | "reservation_expired"
  | "sale_committed"
  | "return_received"
  | "stock_damaged"
  | "stock_restocked"
  | "stock_transfer_in"
  | "stock_transfer_out";

export type InventoryMovementSource =
  | "admin"
  | "seller"
  | "checkout"
  | "payment"
  | "return"
  | "system";

export type InventoryProduct = {
  _id?: string;
  title?: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  thumbnail?: string;
  images?: string[];
  price?: number;
  stockQty?: number;
  status?: string;
  userId?: string;
  sellerUserId?: string;
  brand?: string;
};

export type InventoryStockRow = {
  _id: string;
  productId: string;
  sellerUserId: string;
  warehouseId: string;
  variantKey: string;
  sku: string;

  onHandQty: number;
  reservedQty: number;
  availableQty: number;

  soldQty: number;
  returnedQty: number;
  damagedQty: number;

  lowStockThreshold: number;
  reorderPoint: number;

  status: InventoryStockStatus;

  lastMovementAt?: string;
  createdAt?: string;
  updatedAt?: string;

  product?: InventoryProduct | null;
};

export type InventoryMovement = {
  _id: string;
  productId: string;
  sellerUserId: string;
  warehouseId: string;
  variantKey: string;

  type: InventoryMovementType;
  source: InventoryMovementSource;

  quantity: number;

  beforeOnHandQty: number;
  afterOnHandQty: number;

  beforeReservedQty: number;
  afterReservedQty: number;

  beforeAvailableQty: number;
  afterAvailableQty: number;

  orderId?: string;
  orderItemId?: string;
  reservationId?: string;

  reason?: string;
  note?: string;
  createdBy?: string;

  createdAt?: string;
  updatedAt?: string;
};

export type InventorySummary = {
  totalOnHandQty: number;
  totalReservedQty: number;
  totalAvailableQty: number;
  totalSoldQty: number;
  totalReturnedQty: number;
  totalDamagedQty: number;
  lowStockCount: number;
  outOfStockCount: number;
  stockRowsCount: number;
};

export type InventoryPagination = {
  page: number;
  limit: number;
  total: number;
  pages: number;
};

export type SellerInventoryResponse = {
  rows: InventoryStockRow[];
  pagination: InventoryPagination;
};

export type ProductInventoryResponse = {
  stock: InventoryStockRow[];
  movements: InventoryMovement[];
};

export type InventoryMovementsResponse = {
  rows: InventoryMovement[];
  pagination: InventoryPagination;
};

export type CreateOrUpdateStockPayload = {
  productId: string;
  sellerUserId: string;
  warehouseId?: string;
  variantKey?: string;
  sku?: string;
  quantity?: number;
  setOnHandQty?: number;
  lowStockThreshold?: number;
  reorderPoint?: number;
  reason?: string;
  note?: string;
  createdBy?: string;
  source?: InventoryMovementSource;
};

export type AdjustStockPayload = CreateOrUpdateStockPayload;

export type ValidateCartStockItem = {
  _id?: string;
  id?: string;
  productId?: string;
  sellerUserId?: string;
  ownerId?: string;
  userId?: string;
  dispatchWarehouseId?: string;
  warehouseId?: string;
  quantity?: number;
  title?: string;
  selectedVariants?: any[];
  variantSummary?: string;
  variantKey?: string;
};

export type StockValidationIssue = {
  productId: string;
  sellerUserId: string;
  warehouseId: string;
  variantKey: string;
  title: string;
  requestedQty: number;
  availableQty: number;
  message: string;
};

export type ValidateCartStockResponse = {
  valid: boolean;
  issues: StockValidationIssue[];
};

type ApiResponse<T> = {
  success?: boolean;
  data: T;
  message?: string;
};

export const inventoryApi = createApi({
  reducerPath: "inventoryApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/api/v1`,
  }),
  tagTypes: ["Inventory", "InventorySummary", "InventoryMovements"],
  endpoints: (builder) => ({
    getInventorySummary: builder.query<InventorySummary, string | void>({
      query: (sellerUserId) => ({
        url: "/inventory/summary",
        params: sellerUserId ? { sellerUserId } : undefined,
      }),
      transformResponse: (response: ApiResponse<InventorySummary>) =>
        response.data,
      providesTags: ["InventorySummary"],
    }),

    getSellerInventory: builder.query<
      SellerInventoryResponse,
      {
        sellerUserId: string;
        status?: string;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ sellerUserId, status, search, page = 1, limit = 20 }) => ({
        url: `/inventory/seller/${sellerUserId}`,
        params: {
          status,
          search,
          page,
          limit,
        },
      }),
      transformResponse: (response: ApiResponse<SellerInventoryResponse>) =>
        response.data,
      providesTags: ["Inventory"],
    }),

    getProductInventory: builder.query<ProductInventoryResponse, string>({
      query: (productId) => `/inventory/product/${productId}`,
      transformResponse: (response: ApiResponse<ProductInventoryResponse>) =>
        response.data,
      providesTags: (_result, _error, productId) => [
        { type: "Inventory", id: productId },
        "InventoryMovements",
      ],
    }),

    getLowStockProducts: builder.query<InventoryStockRow[], string>({
      query: (sellerUserId) => `/inventory/low-stock/${sellerUserId}`,
      transformResponse: (response: ApiResponse<InventoryStockRow[]>) =>
        response.data,
      providesTags: ["Inventory"],
    }),

    getInventoryMovements: builder.query<
      InventoryMovementsResponse,
      {
        productId: string;
        sellerUserId?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ productId, sellerUserId, page = 1, limit = 50 }) => ({
        url: `/inventory/movements/${productId}`,
        params: {
          sellerUserId,
          page,
          limit,
        },
      }),
      transformResponse: (response: ApiResponse<InventoryMovementsResponse>) =>
        response.data,
      providesTags: ["InventoryMovements"],
    }),

    createOrUpdateStock: builder.mutation<
      InventoryStockRow,
      CreateOrUpdateStockPayload
    >({
      query: (body) => ({
        url: "/inventory/stock",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<InventoryStockRow>) =>
        response.data,
      invalidatesTags: ["Inventory", "InventorySummary", "InventoryMovements"],
    }),

    adjustStock: builder.mutation<InventoryStockRow, AdjustStockPayload>({
      query: (body) => ({
        url: "/inventory/adjust",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<InventoryStockRow>) =>
        response.data,
      invalidatesTags: ["Inventory", "InventorySummary", "InventoryMovements"],
    }),

    validateCartStock: builder.mutation<
      ValidateCartStockResponse,
      { items: ValidateCartStockItem[] }
    >({
      query: (body) => ({
        url: "/inventory/validate-cart",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<ValidateCartStockResponse>) =>
        response.data,
    }),

    expireReservations: builder.mutation<any[], void>({
      query: () => ({
        url: "/inventory/expire-reservations",
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<any[]>) => response.data,
      invalidatesTags: ["Inventory", "InventorySummary", "InventoryMovements"],
    }),
  }),
});

export const {
  useGetInventorySummaryQuery,
  useGetSellerInventoryQuery,
  useGetProductInventoryQuery,
  useGetLowStockProductsQuery,
  useGetInventoryMovementsQuery,
  useCreateOrUpdateStockMutation,
  useAdjustStockMutation,
  useValidateCartStockMutation,
  useExpireReservationsMutation,
} = inventoryApi;
